import { createApiClient } from '@/api/client';
import type {
    DropdownOptionDTO,
    GetRendersFilters,
    RenderDetailsDTO,
    RenderSummaryDTO,
    RenderVmDTO,
    UpdateRenderPayload,
} from '@/types/render';

export interface CreateRenderMediaPayload {
    id: number;
    name: string;
    sas: string | null;
    order: number;
    mediaType: 1;
    url: string;
}

export interface CreateRenderPayload {
    eventId: number;
    medias: CreateRenderMediaPayload[];
    videoQueType: number;
    start: number;
    stop: number;
}

export async function getRenderVmList(): Promise<RenderVmDTO[]> {
    const api = createApiClient();
    const response = await api.get('/render/vm-list');
    const results = response?.data;

    return Array.isArray(results) ? (results as RenderVmDTO[]) : [];
}

export async function getVideoQueStatusOptions(): Promise<DropdownOptionDTO[]> {
    const api = createApiClient();
    const response = await api.get('/dropdown/videoquestatus');
    const results = response?.data;

    return Array.isArray(results) ? (results as DropdownOptionDTO[]) : [];
}

export async function getVideoQueTypeOptions(): Promise<DropdownOptionDTO[]> {
    const api = createApiClient();
    const response = await api.get('/dropdown/videoquetype');
    const results = response?.data;

    return Array.isArray(results) ? (results as DropdownOptionDTO[]) : [];
}

export async function getRenders(filters: GetRendersFilters = {}): Promise<RenderSummaryDTO[]> {
    const api = createApiClient();
    const params = new URLSearchParams();

    for (const engineType of filters.engineTypes ?? []) {
        params.append('engineTypes', String(engineType));
    }

    if (filters.compressionRerenders) {
        params.append('compressionRerenders', 'true');
    }

    const query = params.toString();
    const endpoint = query.length > 0 ? `/render/v2?${query}` : '/render/v2';
    const response = await api.get(endpoint);
    const results = response?.data;

    return Array.isArray(results) ? (results as RenderSummaryDTO[]) : [];
}

export async function getRenderDetails(renderId: number): Promise<RenderDetailsDTO> {
    const api = createApiClient();
    const response = await api.get(`/render/details/${renderId}`);

    return response.data as RenderDetailsDTO;
}

export async function updateRender(renderId: number, payload: UpdateRenderPayload): Promise<void> {
    const api = createApiClient();
    await api.put(`/Render/${renderId}`, payload);
}

export async function deleteRender(renderId: number): Promise<void> {
    const api = createApiClient();
    await api.delete(`/Render/${renderId}`);
}

export async function updateRenderStatus(renderId: number, status: number): Promise<void> {
    const api = createApiClient();
    await api.post(`/render/update-status?id=${renderId}&status=${status}`);
}

export async function createDetectAndGenerateMutedAudioRender(eventId: number): Promise<void> {
    const api = createApiClient();
    await api.post(`/render/create-detect-and-generate-muted-audio/${eventId}`);
}

export async function createGenerateMutedAudioRender(eventId: number): Promise<void> {
    const api = createApiClient();
    await api.post(`/render/create-generate-muted-audio/${eventId}`);
}

export async function createRender(payload: CreateRenderPayload): Promise<unknown> {
    const api = createApiClient();
    const response = await api.post('/render/create', payload);

    return response.data as unknown;
}

export async function editRender(payload: CreateRenderPayload): Promise<unknown> {
    const api = createApiClient();
    const response = await api.post('/render/edit', payload);

    return response.data as unknown;
}
